import { FC, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import { ICommunityListItemResponse, IHome } from "@miplatform/types";
import * as mdi from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { useCommunityHomesTabViewModel } from "./hooks/useCommunityHomesTabViewModel";
import { AddHomeDrawer } from "../../../components/AddHomeDrawer/AddHomeDrawer";

export const CommunityHomesTab: FC<CommunityHomesTabProps> = ({
  community,
  isLoading,
}) => {
  const { classes } = useStyles();

  const theme = useTheme();

  const { id: communityId } = community || {};

  const {
    homes,
    isLoading: isViewModelLoading,
    getHomesTiedToCommunity,
    removeHomeFromCommunity,
    linkHomeToCommunity,
  } = useCommunityHomesTabViewModel(communityId);

  const [selectedHome, setSelectedHome] = useState<IHome | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRemoveHome = async () => {
    if (selectedHome) {
      setIsDeleteModalOpen(false);
      setSelectedHome(null);
      await removeHomeFromCommunity(selectedHome);
    }
  };

  const handleAddDrawerOpen = () => {
    setIsModalOpen(true);
  };

  const handleLinkHome = (home: IHome) => {
    linkHomeToCommunity(home);
    setIsModalOpen(false);
  };

  const columns = [
    {
      field: "addressLine1",
      headerName: "Address",
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: (p: { row: IHome; value: string }) => (
        <MiLink
          key={p.row.id}
          title={p.value}
          to={`/homes/${p.row.id}/dashboard`}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      headerAlign: "left",
      align: "left",
      flex: 0.7,
    },
    {
      field: "regionCode",
      headerName: "State",
      headerAlign: "left",
      align: "left",
      flex: 0.3,
    },
    {
      field: "community",
      headerName: "Community",
      headerAlign: "left",
      align: "left",
      flex: 0.7,
    },
    {
      field: "builder",
      headerName: "Builder",
      headerAlign: "left",
      align: "left",
      flex: 0.7,
    },
    {
      field: "latitude",
      headerName: "Latitude",
      headerAlign: "left",
      align: "left",
      flex: 0.5,
    },
    {
      field: "longitude",
      headerName: "Longitude",
      headerAlign: "left",
      align: "left",
      flex: 0.5,
    },
    {
      field: "postalCode",
      headerName: "Zip",
      headerAlign: "left",
      align: "left",
      flex: 0.3,
    },
  ];

  const getRowActions = (row: IHome) => [
    {
      text: BUTTON_LABELS.REMOVE,
      onClick: () => {
        setSelectedHome(row);
        setIsDeleteModalOpen(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN as keyof typeof mdi]}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  useEffect(() => {
    if (communityId) {
      getHomesTiedToCommunity();
    }
  }, [communityId]);

  return (
    <MiBox className={classes.viewContainer} height="inherit">
      <MiBuildPageHeader
        title="Homes"
        titleIcon={
          <MiIcon
            path={mdi[ICONS_WEB.HOME as keyof typeof mdi]}
            size={1}
            color={theme.palette.primary.main}
          />
        }
        loading={isLoading}
        actionComponent={
          <MiButton
            title="Home"
            variant="contained"
            startIcon={{
              path: mdi[ICONS_WEB.PLUS as keyof typeof mdi],
            }}
            onClick={handleAddDrawerOpen}
            data-testid="add-home-button"
          />
        }
      />

      <Box className={classes.content}>
        <MiList
          columns={columns}
          data={homes}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading || isViewModelLoading}
          getRowActions={getRowActions}
          emptyListProps={{
            text: "No Homes found",
          }}
        />
      </Box>

      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={isDeleteModalOpen}
        description={`Are you sure you want to remove ${selectedHome?.address.addressLine1} from this community?`}
        disableClick={isLoading || isViewModelLoading}
        handleNegative={() => setIsDeleteModalOpen(false)}
        handlePositive={handleRemoveHome}
        textPositive={BUTTON_LABELS.DELETE}
      />
      {community && (
        <AddHomeDrawer
          title="Add Home to Community"
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onCreate={() => {
            getHomesTiedToCommunity();
            setIsModalOpen(false);
          }}
          communityId={community.id}
          builderId={community.builder?.id}
          readOnlyFields={["communityId"]}
          showHomeSearch
          onCreateWithSelectedHome={handleLinkHome}
          homesFilter={{
            communityId: {
              type: "guid",
              value: null,
            },
          }}
          hideCreateNewOption
        />
      )}
    </MiBox>
  );
};

interface CommunityHomesTabProps {
  community: ICommunityListItemResponse;
  isLoading?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  viewContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
  },
}));
